import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Mail, RefreshCw, Trash2 } from 'lucide-react';

export default function AdminContactMessages({
  messages,
  statusCounts,
  filter,
  setFilter,
  onLoadMessages,
  onUpdateStatus,
  updatingMessage,
  onDeleteMessage,
}) {
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  const handleOpenDetail = (msg) => {
    setSelectedContact({ ...msg });
    setShowDetailDialog(true);
  };

  const handleSaveChanges = async () => {
    if (selectedContact) {
      await onUpdateStatus(selectedContact.id, selectedContact.status, selectedContact.admin_notes);
      setShowDetailDialog(false);
    }
  };

  const handleDelete = () => {
    if (selectedContact) {
      onDeleteMessage(selectedContact.id);
      setShowDetailDialog(false);
    }
  };

  return (
    <>
      <Card className="rounded-sm bg-slate-800 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-400" />
            Contact Messages
            {statusCounts.new > 0 && (
              <Badge className="bg-blue-600 ml-2">{statusCounts.new} new</Badge>
            )}
          </CardTitle>
          <CardDescription className="text-slate-400">
            Messages from the contact form
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Status Summary */}
          <div className="flex gap-2 mb-4">
            <Badge className="bg-blue-600">{statusCounts.new || 0} New</Badge>
            <Badge className="bg-yellow-600">{statusCounts.read || 0} Read</Badge>
            <Badge className="bg-green-600">{statusCounts.responded || 0} Responded</Badge>
            <Badge className="bg-slate-600">{statusCounts.closed || 0} Closed</Badge>
          </div>

          <div className="flex items-center justify-between mb-4">
            <Select 
              value={filter.status || 'all'} 
              onValueChange={(val) => setFilter({ status: val === 'all' ? '' : val })}
            >
              <SelectTrigger className="w-32 rounded-sm bg-slate-700 border-slate-600 text-white text-sm">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="read">Read</SelectItem>
                <SelectItem value="responded">Responded</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={onLoadMessages} className="rounded-sm border-slate-600">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {messages.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No contact messages yet</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className="p-3 bg-slate-700 rounded-sm hover:bg-slate-600 cursor-pointer"
                  onClick={() => handleOpenDetail(msg)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-200">{msg.subject}</span>
                      </div>
                      <p className="text-sm text-slate-300 mt-1">{msg.name} &lt;{msg.email}&gt;</p>
                      <p className="text-xs text-slate-400 mt-1 line-clamp-1">{msg.message}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                        {msg.company && <span>{msg.company}</span>}
                        <span>{new Date(msg.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <Badge className={
                      msg.status === 'new' ? 'bg-blue-600' :
                      msg.status === 'read' ? 'bg-yellow-600' :
                      msg.status === 'responded' ? 'bg-green-600' : 'bg-slate-600'
                    }>
                      {msg.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contact Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-400" />
              Contact Message Details
            </DialogTitle>
          </DialogHeader>
          {selectedContact && (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-400 text-xs">Subject</Label>
                <p className="font-medium">{selectedContact.subject}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400 text-xs">From</Label>
                  <p className="text-sm">{selectedContact.name}</p>
                  <p className="text-xs text-blue-400">{selectedContact.email}</p>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Status</Label>
                  <Select
                    value={selectedContact.status}
                    onValueChange={(val) => setSelectedContact({ ...selectedContact, status: val })}
                  >
                    <SelectTrigger className="w-full mt-1 rounded-sm bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="read">Read</SelectItem>
                      <SelectItem value="responded">Responded</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {(selectedContact.company || selectedContact.phone) && (
                <div className="grid grid-cols-2 gap-4">
                  {selectedContact.company && (
                    <div>
                      <Label className="text-slate-400 text-xs">Company</Label>
                      <p className="text-sm">{selectedContact.company}</p>
                    </div>
                  )}
                  {selectedContact.phone && (
                    <div>
                      <Label className="text-slate-400 text-xs">Phone</Label>
                      <p className="text-sm">{selectedContact.phone}</p>
                    </div>
                  )}
                </div>
              )}
              <div>
                <Label className="text-slate-400 text-xs">Message</Label>
                <p className="text-sm bg-slate-700 p-3 rounded-sm whitespace-pre-wrap max-h-48 overflow-y-auto">{selectedContact.message}</p>
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Submitted</Label>
                <p className="text-sm">{new Date(selectedContact.created_at).toLocaleString()}</p>
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Admin Notes</Label>
                <Textarea
                  value={selectedContact.admin_notes || ''}
                  onChange={(e) => setSelectedContact({ ...selectedContact, admin_notes: e.target.value })}
                  placeholder="Add notes about this message..."
                  className="mt-1 rounded-sm bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div className="flex justify-between pt-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  className="rounded-sm"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowDetailDialog(false)} className="rounded-sm border-slate-600">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveChanges}
                    disabled={updatingMessage}
                    className="rounded-sm bg-blue-600 hover:bg-blue-700"
                  >
                    {updatingMessage ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
